import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AlvnaBrand from "./AlvnaBrand";
import { ContactSupportModal, SetupModal } from "./SupportModals";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { getSessionType, getAuthHeader } from "../session";
import { colors, fonts, space, radius } from "../theme";
import useIsMobile from "../useIsMobile";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: DashboardIcon, tenantOnly: true, featureFlag: "dashboard_enabled" },
  { to: "/menu", label: "Menu", icon: MenuIcon },
  { to: "/tables", label: "Tables", icon: TablesIcon },
  { to: "/staff", label: "Staff", icon: StaffIcon },
  { to: "/reports", label: "Reports", icon: ReportsIcon, tenantOnly: true, featureFlag: "reports_enabled" },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Sidebar({ businessName }) {
  const [attendanceOn, setAttendanceOn] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem("tenant") || "null")?.attendance_enabled; } catch (e) { return false; }
  });
  useEffect(() => {
    if (!getAuthHeader().Authorization) return;
    axios.get(endpoints.settings, { headers: getAuthHeader() })
      .then((res) => { setAttendanceOn(!!res.data.attendance_enabled); setOnlineOn(!!res.data.online_ordering_enabled); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!getAuthHeader().Authorization) return;
    axios.get(endpoints.featureToggles, { headers: getAuthHeader() })
      .then((res) => { 
        setDashboardEnabled(res.data.dashboard_enabled);
        setReportsEnabled(res.data.reports_enabled);
      })
      .catch(() => {});
  }, []);
  const [onlineOn, setOnlineOn] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem("tenant") || "null")?.online_ordering_enabled; } catch (e) { return false; }
  });
  const [dashboardEnabled, setDashboardEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tenant") || "null")?.dashboard_enabled !== false; } catch (e) { return true; }
  });
  const [reportsEnabled, setReportsEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tenant") || "null")?.reports_enabled !== false; } catch (e) { return true; }
  });
  const [showContact, setShowContact] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const isManager = getSessionType() === "manager";
  const currentPath = window.location.pathname;

  const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
  const tenantEmail = tenant?.email;

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("tenant");
    sessionStorage.removeItem("staff_token");
    sessionStorage.removeItem("staff");
    sessionStorage.removeItem("staff_tenant");
    window.location.href = isManager ? "/manager-login" : "/login";
  };

  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Leave the drawer closed when the screen grows past the phone breakpoint,
  // and let Escape close it.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  const nav = (
    <nav style={styles.nav}>
      {[...NAV_ITEMS.slice(0, 4), ...(attendanceOn ? [{ to: "/employees", label: "Employees", icon: StaffIcon }, { to: "/attendance", label: "Attendance", icon: ClockIcon }] : []), ...(onlineOn ? [{ to: "/online-ordering", label: "Online Ordering", icon: OrderIcon }] : []), ...NAV_ITEMS.slice(4)].filter((item) => {
        // Filter out tenantOnly items for managers
        if (item.tenantOnly && isManager) return false;
        // Filter out ownerOnly items for managers
        if (item.ownerOnly && isManager) return false;
        // Filter out Dashboard if disabled
        if (item.to === "/dashboard" && !dashboardEnabled) return false;
        // Filter out Reports if disabled
        if (item.to === "/reports" && !reportsEnabled) return false;
        return true;
      }).map(({ to, label, icon: Icon }) => {
        const active = currentPath === to;
        return (
          <Link key={to} to={to} onClick={closeDrawer} style={{ ...styles.link, ...(active ? styles.linkActive : {}) }}>
            <Icon active={active} />
            {label}
          </Link>
        );
      })}
      <Link to="/manager-home" onClick={closeDrawer} style={styles.opsLink}>
        <OperationsIcon />
        Waiter &amp; Kitchen
      </Link>
    </nav>
  );

  const footer = (
    <div style={styles.footer}>
      <button style={styles.footerBtn} onClick={() => { closeDrawer(); setShowContact(true); }}>Contact Support</button>
      <button style={styles.footerBtn} onClick={() => { closeDrawer(); setShowSetup(true); }}>Setup Guide</button>
      <button style={styles.logoutBtn} onClick={logout}>Log Out</button>
    </div>
  );

  // Modals live outside the drawer: the drawer is transformed, and a
  // transformed ancestor would pin position:fixed children to itself.
  const modals = (
    <>
      {showContact && (
        <ContactSupportModal onClose={() => setShowContact(false)} prefillEmail={tenantEmail} />
      )}
      {showSetup && <SetupModal onClose={() => setShowSetup(false)} />}
    </>
  );

  if (isMobile) {
    return (
      <>
        <div style={styles.topBar}>
          <button
            className="icon-btn"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            style={styles.menuBtn}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/alvna-logo.png" alt="Alvna" style={styles.topBarLogo} />
          <span style={styles.topBarName}>{businessName}</span>
        </div>

        {drawerOpen && <div style={styles.backdrop} onClick={closeDrawer} data-testid="drawer-backdrop" />}
        <div
          role="dialog"
          aria-label="Menu"
          aria-hidden={!drawerOpen}
          style={{ ...styles.drawer, transform: drawerOpen ? "translateX(0)" : "translateX(-105%)", visibility: drawerOpen ? "visible" : "hidden" }}
        >
          <div>
            <AlvnaBrand variant="full" businessName={businessName} dark />
            {nav}
          </div>
          {footer}
        </div>
        {modals}
      </>
    );
  }

  return (
    <div style={styles.sidebar}>
      <AlvnaBrand variant="full" businessName={businessName} dark />
      {nav}
      {footer}
      {modals}
    </div>
  );
}

// Simple, crisp inline icons — no external icon library needed.
function iconProps(active) {
  return {
    width: 18, height: 18, viewBox: "0 0 24 24", fill: "none",
    stroke: active ? "#fff" : colors.textMuted, strokeWidth: 2,
    strokeLinecap: "round", strokeLinejoin: "round",
  };
}
function DashboardIcon({ active }) {
  return <svg {...iconProps(active)}><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>;
}
function MenuIcon({ active }) {
  return <svg {...iconProps(active)}><path d="M4 6h16M4 12h16M4 18h16" /></svg>;
}
function TablesIcon({ active }) {
  return <svg {...iconProps(active)}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" /></svg>;
}
function StaffIcon({ active }) {
  return <svg {...iconProps(active)}><circle cx="9" cy="7" r="4" /><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /><path d="M17 3.5a4 4 0 0 1 0 7.5" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>;
}

function ClockIcon({ active }) {
  return <svg {...iconProps(active)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
}

function OrderIcon({ active }) {
  return <svg {...iconProps(active)}><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /><path d="M3 4h2l2.5 11h10.5l2-8H6" /></svg>;
}
function ReportsIcon({ active }) {
  return <svg {...iconProps(active)}><path d="M3 3v18h18" /><path d="M18 17V9M13 17V5M8 17v-3" /></svg>;
}
function SettingsIcon({ active }) {
  return <svg {...iconProps(active)}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>;
}
function OperationsIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M9 21v-6h6v6" /></svg>;
}

const styles = {
  topBar: {
    position: "fixed", top: 0, left: 0, right: 0, height: 56, zIndex: 900,
    background: colors.darkBg, borderBottom: `1px solid ${colors.darkBorder}`,
    display: "flex", alignItems: "center", gap: space[3], padding: `0 ${space[3]}`,
  },
  menuBtn: {
    width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
    background: "none", border: `1px solid ${colors.darkBorder}`, borderRadius: radius.sm,
    color: colors.darkText, cursor: "pointer", flexShrink: 0,
  },
  topBarLogo: { height: 26, width: "auto", flexShrink: 0 },
  topBarName: {
    fontFamily: fonts.body, fontSize: 14, fontWeight: 600, color: colors.darkText,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
  },
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1090 },
  drawer: {
    position: "fixed", top: 0, bottom: 0, left: 0, width: 268, maxWidth: "84vw", zIndex: 1100,
    background: colors.darkBg, borderRight: `1px solid ${colors.darkBorder}`,
    display: "flex", flexDirection: "column", justifyContent: "space-between",
    overflowY: "auto", transition: "transform 0.2s ease, visibility 0.2s",
  },
  sidebar: {
    width: 232,
    minHeight: "100vh",
    background: colors.darkBg,
    borderRight: `1px solid ${colors.darkBorder}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  nav: { display: "flex", flexDirection: "column", padding: `${space[4]} ${space[3]}`, gap: 2 },
  link: {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    padding: "12px 14px",
    borderRadius: radius.sm,
    color: colors.textMuted,
    textDecoration: "none",
    fontFamily: fonts.body,
    fontSize: 14,
    fontWeight: 500,
    transition: "background 0.12s ease, color 0.12s ease",
  },
  linkActive: {
    background: colors.brandGradient,
    color: "#fff",
  },
  opsLink: {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    padding: "12px 14px",
    borderRadius: radius.sm,
    color: "#c4b5fd",
    textDecoration: "none",
    fontFamily: fonts.body,
    fontSize: 14,
    fontWeight: 600,
    marginTop: space[3],
    border: "1px solid #4c3d8f",
    background: "rgba(139,92,246,0.08)",
  },
  footer: { padding: space[3], display: "flex", flexDirection: "column", gap: space[2] },
  footerBtn: {
    padding: "10px 14px",
    fontFamily: fonts.body,
    fontSize: 13,
    border: `1px solid ${colors.darkBorder}`,
    borderRadius: radius.sm,
    background: colors.darkSurface,
    color: colors.darkText,
    cursor: "pointer",
  },
  logoutBtn: {
    padding: "10px 14px",
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 600,
    border: "1px solid #7f1d1d",
    borderRadius: radius.sm,
    background: "rgba(127,29,29,0.2)",
    color: "#fca5a5",
    cursor: "pointer",
  },
};
